/**
 * Keyboard shortcut registry (desktop only). Same model as saas-books useShortcuts.ts:
 * default combos per action id, user overrides kept in localStorage.
 *
 * Combo format: "Ctrl+Shift+L", "N", "/", "[", "G H" (a space means a two-key sequence).
 * "Ctrl" matches Ctrl on Windows/Linux and ⌘ on Mac.
 */
export const SHORTCUT_CATEGORIES = ["Global", "Go to", "Lists", "Detail", "Dialogs"];

export const DEFAULT_SHORTCUTS = [
  { id: "toggle_sidebar", label: "Collapse / expand sidebar", category: "Global", defaultCombo: "Ctrl+B" },
  { id: "toggle_theme", label: "Toggle light / dark theme", category: "Global", defaultCombo: "Ctrl+Shift+L" },
  { id: "show_help", label: "Show keyboard shortcuts", category: "Global", defaultCombo: "?" },
  { id: "go_home", label: "Go to Home", category: "Go to", defaultCombo: "G H" },
  { id: "go_invoices", label: "Go to Sales", category: "Go to", defaultCombo: "G S" },
  { id: "go_purchases", label: "Go to Purchases", category: "Go to", defaultCombo: "G P" },
  { id: "go_items", label: "Go to Items", category: "Go to", defaultCombo: "G I" },
  { id: "go_customers", label: "Go to Parties", category: "Go to", defaultCombo: "G C" },
  { id: "go_inventory", label: "Go to Stock", category: "Go to", defaultCombo: "G K" },
  { id: "go_banking", label: "Go to Banking", category: "Go to", defaultCombo: "G B" },
  { id: "go_settings", label: "Go to Settings", category: "Go to", defaultCombo: "G ," },
  { id: "list_new", label: "New record", category: "Lists", defaultCombo: "N" },
  { id: "list_search", label: "Focus search", category: "Lists", defaultCombo: "/" },
  { id: "list_toggle_view", label: "Switch table / cards view", category: "Lists", defaultCombo: "V" },
  { id: "record_prev", label: "Previous record", category: "Detail", defaultCombo: "[" },
  { id: "record_next", label: "Next record", category: "Detail", defaultCombo: "]" },
  { id: "record_close", label: "Close detail pane", category: "Detail", defaultCombo: "Escape" }
];

/** Fixed keys shown in the help but not customisable (handled by the components themselves). */
export const FIXED_SHORTCUTS = [
  { label: "Move between table rows", category: "Lists", combo: "↑ ↓" },
  { label: "Open the focused row", category: "Lists", combo: "Enter" },
  { label: "Close the open dialog", category: "Dialogs", combo: "Escape" }
];

const STORAGE_KEY = "books_keyboard_shortcuts";
export const SHORTCUTS_CHANGED_EVENT = "books-shortcuts-changed";

export const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

function readOverrides() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeOverrides(overrides) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  window.dispatchEvent(new CustomEvent(SHORTCUTS_CHANGED_EVENT));
}

/** Effective combo for an action (user override, else default). */
export function getCombo(id) {
  const overrides = readOverrides();
  if (overrides[id]) return overrides[id];
  return DEFAULT_SHORTCUTS.find((s) => s.id === id)?.defaultCombo || "";
}

export function isCustomized(id) {
  return Boolean(readOverrides()[id]);
}

export function saveShortcutOverride(id, combo) {
  writeOverrides({ ...readOverrides(), [id]: combo });
}

export function resetShortcut(id) {
  const overrides = readOverrides();
  delete overrides[id];
  writeOverrides(overrides);
}

export function resetAllShortcuts() {
  writeOverrides({});
}

/** Action id already using `combo` (other than `exceptId`), or null. */
export function findConflict(combo, exceptId) {
  const normalized = combo.toLowerCase();
  const hit = DEFAULT_SHORTCUTS.find((s) => s.id !== exceptId && getCombo(s.id).toLowerCase() === normalized);
  return hit || null;
}

/** "Ctrl+Shift+L" → ["⌘", "⇧", "L"] on Mac, ["Ctrl", "Shift", "L"] elsewhere. Sequences keep "then". */
export function formatCombo(combo) {
  if (!combo) return [];
  return combo.split(" ").flatMap((step, i) => {
    const keys = step.split("+").map((key) => {
      if (key === "Ctrl") return IS_MAC ? "⌘" : "Ctrl";
      if (key === "Shift") return IS_MAC ? "⇧" : "Shift";
      if (key === "Alt") return IS_MAC ? "⌥" : "Alt";
      if (key === "Escape") return "Esc";
      return key.length === 1 ? key.toUpperCase() : key;
    });
    return i === 0 ? keys : ["then", ...keys];
  });
}

/** Readable hint for tooltips, e.g. "Ctrl+B" / "⌘B" / "G then S". */
export function comboHint(id) {
  return formatCombo(getCombo(id)).join(IS_MAC ? "" : "+").replace(/\+?then\+?/g, " then ");
}

/** Combo string for a keyboard event, e.g. "Ctrl+Shift+L". Null for bare modifier presses. */
export function comboFromEvent(e) {
  if (["Control", "Shift", "Alt", "Meta"].includes(e.key)) return null;
  const parts = [];
  if (e.ctrlKey || e.metaKey) parts.push("Ctrl");
  if (e.altKey) parts.push("Alt");
  const key = normalizeKey(e);
  // Shift is implied by printable symbols like "?" — only record it for letters/named keys
  if (e.shiftKey && (key.length > 1 || /[A-Z]/i.test(key))) parts.push("Shift");
  parts.push(key);
  return parts.join("+");
}

function normalizeKey(e) {
  if (e.key === " ") return "Space";
  if (e.key.length === 1) return /[a-z]/i.test(e.key) ? e.key.toUpperCase() : e.key;
  return e.key;
}

/** Whether a keyboard event matches one step of a combo ("Ctrl+B", "?", "N"). */
export function eventMatchesStep(e, step) {
  const parts = step.split("+");
  const key = parts.pop();
  const wantCtrl = parts.includes("Ctrl");
  const wantAlt = parts.includes("Alt");
  const wantShift = parts.includes("Shift");
  if ((e.ctrlKey || e.metaKey) !== wantCtrl) return false;
  if (e.altKey !== wantAlt) return false;
  const pressed = normalizeKey(e);
  if (pressed.toLowerCase() !== key.toLowerCase()) return false;
  // Shift must match for letters and named keys; symbols (?, /, [) carry their own shift state
  if (key.length > 1 || /[a-z]/i.test(key)) return e.shiftKey === wantShift;
  return true;
}

/** True while the user is typing in a field — single-key shortcuts must not fire then. */
export function isInputActive() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}
